/* eslint-disable complexity */
/* eslint-disable max-lines */

import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import {
    capitalizeString,
    conditionalString,
    DateFormats,
    formatDate,
    formatNameLastFirst,
    openInNewTab,
} from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { formatRateString } from '@strata/react-servicing-rate';
import { Badge } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { isTaskTodo, Worklist, type WorklistTask } from '@strata/react-worklist';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import {
    type Location,
    useLocation,
    useNavigate,
    useNavigationType,
    useParams,
} from 'react-router';

import { ReferralAndOnboardingStatusIndicator } from './ReferralAndOnboardingStatusIndicator';

import { LogFillActivityDialog } from 'components/shared/dialogs/LogFillActivityDialog';
import { FillActivity } from 'components/shared/FillActivity';
import { HealthPlanNotes } from 'components/shared/HealthPlanNotes';
import { MultiStrengthTooltip } from 'components/shared/MultiStrengthTooltip';
import { Performance } from 'components/shared/Performance';
import { ProductLabel } from 'components/shared/ProductLabel';
import { ClaimsAndEstimates } from 'components/wonReferrals/ClaimsAndEstimates';
import { ConvertBiosimilarDialog } from 'components/wonReferrals/dialogs/ConvertBiosimilarDialog';
import { PendingIntakeDialog } from 'components/wonReferrals/dialogs/PendingIntakeDialog';
import { RebillingDialog } from 'components/wonReferrals/dialogs/RebillingDialog';
import { UpdateNeedsByDateDialog } from 'components/wonReferrals/dialogs/UpdateNeedsByDateDialog';
import { EscalateClinicalConcern } from 'components/wonReferrals/EscalateClinicalConcern';
import { Messages } from 'components/wonReferrals/Messages';
import { NoticeBoard } from 'components/wonReferrals/NoticeBoard';
import { PatientDetails } from 'components/wonReferrals/PatientDetails';
import { PerformanceProgress } from 'components/wonReferrals/PerformanceProgress';
import { PrescriberDetails } from 'components/wonReferrals/PrescriberDetails';
import { ReferralDetails } from 'components/wonReferrals/ReferralDetails';
import { ServicingDetails } from 'components/wonReferrals/ServicingDetails';
import { BlockTasksDialog } from 'components/worklist/dialogs/BlockTasksDialog';
import {
    GRANDFATHERED_REFERRAL_DETAILS_BANNER,
    isGrandfatheredReferral,
} from 'constants/grandfatheredReferral';
import {
    usePermissionCheck,
    useReferralDetails,
    useSelectedReferral,
    useUserSupportConversations,
    useWorklistTasks,
} from 'services/APIHooks';
import { PendingReferralStatus, Permissions } from 'services/constants';
import {
    PayerFeatures,
    SupplierFeatures,
    usePayerFeatures,
    useSupplierFeatures,
} from 'services/FeatureFlags';
import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { canDeclinePendingReferral } from 'services/referralHelpers';
import { AppRoutes, type BackToDestination, useCurrentReferralLocation } from 'services/routes';
import {
    type ConversationContext,
    ConversationContexts,
    MESSAGES_LOCATION_HASH,
} from 'services/userSupportHelpers';
import {
    showDialogForWorklistTypes,
    useOnDoNotConvert,
    useOnTaskActionClick,
    useOnUnableToComplete,
} from 'services/worklistHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import {
    useGetBiosimilarConversionProductsQuery,
    useGetPayerSegmentsQuery,
    useGetPlaceOfServiceMapQuery,
    useGetPrescriberDetailsQuery,
    useGetProductsMapQuery,
} from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';
import { useGetTherapyGroupContainmentConfigQuery } from 'store/api/treatmentApi';
import { useGetFillActivitiesQuery } from 'store/api/userSupportApi';
import { usePostClearWorklistTasksMutation } from 'store/api/worklistApi';
import { fetchPerformanceEntries } from 'store/wonReferrals/performanceThunks';
import { type PendingReferral } from 'store/wonReferrals/wonReferralListSlice';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

export const WonReferralDetailsPage = () => {
    const { referralId } = useParams() as { referralId: string };
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const location = useLocation() as Location<{
        worklistTask?: WorklistTask;
        backToDestination?: BackToDestination;
        beginSupportConversation?: boolean;
        beginConversationContext?: ConversationContext;
    }>;
    const dispatch = useAppDispatch();
    const {
        marketplaceEnhancedEligibility,
        showConvertBiosimilarActionPharmacy,
        showGrandfatheredPharmacy,
    } = useFlags<{
        marketplaceEnhancedEligibility: boolean;
        showConvertBiosimilarActionPharmacy: boolean;
        showGrandfatheredPharmacy: boolean;
    }>();

    const selectedReferral = useSelectedReferral({ referralId });
    const payerId = selectedReferral?.payerId;
    const referralDetails = useReferralDetails(referralId, payerId);

    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);
    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);
    const writeBiosimilarConversion = usePermissionCheck(Permissions.WRITE_BIOSIMILAR_CONVERSION);
    const supplierFeatures = useSupplierFeatures();
    const payerFeatures = usePayerFeatures(payerId);

    const isPendingIntake =
        selectedReferral?.referralStatus === ReferralStatusIndicator.Statuses.PENDINTAKE;
    const pendingStatus = selectedReferral?.pendingStatus;
    const isExpired = dayjs().isAfter(selectedReferral?.referralExpiration);
    const canDeclinePending = canDeclinePendingReferral(selectedReferral?.routeCode);
    const isPendingOutreach = dayjs().isBefore(referralDetails?.outreachDate);
    const isPendingStart = dayjs().isBefore(referralDetails?.referralStart);
    const showGrandfatheredBanner = isGrandfatheredReferral(
        referralDetails,
        showGrandfatheredPharmacy,
    );

    const backToDestination = (() => {
        if (location.state?.backToDestination) {
            return location.state.backToDestination;
        }
        if (isExpired) {
            return { text: 'Expired Referrals', route: AppRoutes.expiredReferrals };
        }
        if (isPendingStart) {
            return { text: 'Upcoming Referrals', route: AppRoutes.upcomingReferrals };
        }
        return { text: 'My Referrals', route: AppRoutes.wonReferrals };
    })();
    const goBack = useCallback(
        () => navigate(backToDestination.route),
        [navigate, backToDestination.route],
    );

    // navigate back to referral list if referralId is invalid
    useCurrentReferralLocation(backToDestination.route);

    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();
    const { data: productMap } = useGetProductsMapQuery();
    const { data: segmentData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segmentData ?? {};
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        referralDetails?.prescriberNpi ? { npi: referralDetails.prescriberNpi } : skipToken,
    );
    const memberDetailsQueryArgs =
        marketplaceEnhancedEligibility &&
        referralDetails?.memberId &&
        referralDetails.payerId &&
        referralDetails.memberFirstName &&
        referralDetails.memberLastName
            ? {
                  segmentId: referralDetails.segmentId,
                  memberId: referralDetails.memberId,
                  firstName: referralDetails.memberFirstName,
                  lastName: referralDetails.memberLastName,
                  dateOfBirth: referralDetails.memberDOB,
                  payerId: referralDetails.payerId,
              }
            : skipToken;
    const { data: memberDetails } = useGetMemberDetailsQuery(memberDetailsQueryArgs);
    const conversionMemberState = memberDetails?.memberState ?? referralDetails?.memberState;
    const { data: therapyGroupContainmentConfig } = useGetTherapyGroupContainmentConfigQuery(
        showConvertBiosimilarActionPharmacy &&
            writeBiosimilarConversion &&
            referralDetails?.payerId &&
            referralDetails?.segmentId
            ? { payerId: referralDetails.payerId, segmentId: referralDetails.segmentId }
            : skipToken,
    );
    const canRequestBiosimilarConversion =
        !!showConvertBiosimilarActionPharmacy &&
        !!writeBiosimilarConversion &&
        !!therapyGroupContainmentConfig?.segmentTherapyGroupContainmentOn;
    const { data: biosimilarConversionOptions } = useGetBiosimilarConversionProductsQuery(
        canRequestBiosimilarConversion &&
            referralDetails?.productServiceOid &&
            conversionMemberState
            ? {
                  productServiceOid: referralDetails.productServiceOid,
                  payerId: referralDetails.payerId,
                  segmentId: referralDetails.segmentId,
                  memberState: conversionMemberState,
                  benefit: referralDetails.benefit,
                  placeOfService: referralDetails.placeOfService,
              }
            : skipToken,
    );
    const productConfig = useProductConfig({
        segmentId: referralDetails?.segmentId,
        productServiceOid: referralDetails?.productServiceOid,
        benefit: referralDetails?.benefit,
    });

    // Hide full patient details for pending & upcoming referrals (except grandfathered)
    const showFullPatientDetails = !isPendingOutreach || showGrandfatheredBanner;
    const showFillActivity = !isPendingIntake && !isPendingOutreach && !isExpired;

    const { data: events, isFetching: eventsFetching } = useGetFillActivitiesQuery(
        showFillActivity
            ? {
                  referralId,
              }
            : skipToken,
    );

    // Only show Claim Submission option for Medical Billing Benefit
    const isMedicalBilling = referralDetails?.benefit === BenefitTypes.MEDICAL;

    // Need both supplier & payer MEDBILLING features to bill claims
    const showMedBillingSections =
        supplierFeatures?.[SupplierFeatures.MEDBILLING] &&
        payerFeatures?.[PayerFeatures.MEDBILLING] &&
        isMedicalBilling;

    const performanceEntries = useAppSelector(
        (state) => state.performance?.performanceEntries?.[referralId],
    );
    useEffect(() => {
        dispatch(fetchPerformanceEntries(referralId));
    }, [dispatch, referralId, payerId]);

    const [showRenewalDialog, setShowRenewalDialog] = useState(
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.PENDINTAKE, Worklist.WorkTaskType.ACCEPTANCE],
            navigationType,
            location,
        }),
    );
    const [showNewConversationDialog, setShowNewConversationDialog] = useState<
        ConversationContext | boolean
    >(
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.MANSUBTASK],
            navigationType,
            location,
        }) && location.state?.beginSupportConversation
            ? ConversationContexts.PATIENT_DISCONTINUED_THERAPY
            : (location.state?.beginConversationContext ?? false),
    );
    const [showNeedsByDateDialog, setShowNeedsByDateDialog] = useState(
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.NBD],
            navigationType,
            location,
        }),
    );

    const { conversationList, referralConversationCounts } = useUserSupportConversations(
        referralDetails?.referralId,
    );
    const startConversation = (context: ConversationContext | boolean = true) => {
        if ((location.hash ?? '').substring(1) !== MESSAGES_LOCATION_HASH) {
            navigate({ hash: MESSAGES_LOCATION_HASH }, { state: location.state });
        }
        setShowNewConversationDialog(context);
    };

    const writeOnboarding = usePermissionCheck(Permissions.WRITE_ONBOARDING);
    const [showLogFillActivityDialog, setShowLogFillActivityDialog] = useState(false);
    const [showConvertBiosimilarDialog, setShowConvertBiosimilarDialog] = useState<
        WorklistTask | boolean
    >(() =>
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.CONVERSION],
            navigationType,
            location,
        })
            ? (location.state?.worklistTask ?? false)
            : false,
    );
    const onDoNotConvert = useOnDoNotConvert();
    const [taskToBlock, setTaskToBlock] = useState<WorklistTask>();

    const canShowConvertBiosimilar =
        canRequestBiosimilarConversion &&
        !!referralDetails?.treatmentId &&
        !referralDetails.grandfathered &&
        !isExpired &&
        !isPendingIntake &&
        !isPendingStart &&
        (biosimilarConversionOptions?.length ?? 0) > 0;

    const menuOptionsAndConditions = [
        {
            condition: pendingStatus === PendingReferralStatus.PENDING,
            text: `Accept${canDeclinePending ? '/Decline' : ''} Referral`,
            onItemClick: () => setShowRenewalDialog(true),
        },
        {
            condition: canShowConvertBiosimilar,
            text: ConvertBiosimilarDialog.Title,
            onItemClick: () => setShowConvertBiosimilarDialog(true),
        },
        {
            condition: !!referralDetails,
            text: 'Create Support Conversation',
            onItemClick: () => startConversation(),
        },
        {
            condition: !!referralDetails && showFillActivity && writeOnboarding,
            text: 'Log Fill Activity',
            onItemClick: () => setShowLogFillActivityDialog(true),
        },
        {
            condition:
                writeMedBilling && showMedBillingSections && !(isPendingIntake || isPendingStart),
            text: 'Submit Estimate/Claim',
            onItemClick: () => navigate(AppRoutes.claimSubmissionForm(referralId)),
        },
        {
            condition:
                !(isExpired || isPendingIntake || isPendingStart) &&
                referralDetails?.benefit === BenefitTypes.PHARMACY,
            text: 'Update Next Fill Needs By Date',
            onItemClick: () => setShowNeedsByDateDialog(true),
        },
    ];
    const menuItems = menuOptionsAndConditions.filter(({ condition }) => condition);

    const additionalMenuItems = [
        {
            text: 'See Terms & Conditions',
            onItemClick: () => openInNewTab(AppRoutes.termsAndConditions),
        },
    ];

    const getPendingReferralInlineMessage = () => {
        if (pendingStatus === PendingReferralStatus.PENDING) {
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.WARNING}
                    label={`This referral is pending intake. Accept${conditionalString(
                        canDeclinePending,
                        '/decline',
                    )} from the actions menu before ${formatDate(
                        selectedReferral?.pendingExpiration,
                        DateFormats.DATE_TIME_LONG,
                    )}.`}
                />
            );
        }
        if (pendingStatus === PendingReferralStatus.ACCEPTED) {
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.SUCCESS}
                    label={`Referral was accepted at ${formatRateString({
                        bidDecimal: selectedReferral?.pendingAcceptedRate,
                        referentPriceType: selectedReferral?.referentPriceType,
                    })}.`}
                />
            );
        }
        if (pendingStatus === PendingReferralStatus.DECLINED) {
            return (
                <InlineMessage intent={InlineMessage.Intent.ERROR} label="Referral was declined." />
            );
        }
        return null;
    };

    const getGrandfatheredReferralInlineMessage = () => {
        if (!showGrandfatheredBanner) {
            return null;
        }

        return (
            <InlineMessage
                intent={InlineMessage.Intent.INFO}
                label={GRANDFATHERED_REFERRAL_DETAILS_BANNER}
                wrapText
            />
        );
    };

    const getProspectiveReferralInlineMessage = () => {
        if (isPendingOutreach) {
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.WARNING}
                    label={`Servicing of this referral cannot begin until the servicing start date of ${formatDate(referralDetails?.referralStart, DateFormats.DATE_MEDIUM)}. Prescriber and member outreach can begin ${formatDate(referralDetails?.outreachDate, DateFormats.DATE_MEDIUM)}.`}
                />
            );
        }
        if (isPendingStart) {
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.WARNING}
                    label={`Servicing of this referral cannot begin until the servicing start date of ${formatDate(referralDetails?.referralStart, DateFormats.DATE_MEDIUM)}. Prescriber and member outreach can begin now.`}
                />
            );
        }
        return null;
    };

    const getRowThreeContentHeader = () => {
        const grandfatheredMessage = getGrandfatheredReferralInlineMessage();
        const prospectiveMessage = getProspectiveReferralInlineMessage();
        if (!grandfatheredMessage && !prospectiveMessage) {
            return undefined;
        }
        return (
            <>
                {grandfatheredMessage}
                {prospectiveMessage}
            </>
        );
    };

    const getPerformanceComponent = () => {
        if (!(isPendingIntake || isPendingStart) && performanceEntries) {
            return <PerformanceProgress performanceEntries={performanceEntries} />;
        }
        if (selectedReferral?.valueCases && selectedReferral.valueCases.length > 0) {
            return <Performance selectedReferral={selectedReferral} />;
        }
        return null;
    };

    const referralWorklistTasks = useWorklistTasks()?.filter(
        (task) => task.referral_id === referralId,
    );
    const openConversionTask = referralWorklistTasks?.find(
        (task) => isTaskTodo(task) && task.task_type === Worklist.WorkTaskType.CONVERSION,
    );
    const toDoReferralWorklistTasks = referralWorklistTasks?.filter((task) => isTaskTodo(task));
    const onTaskActionClick = useOnTaskActionClick();
    const onUnableToComplete = useOnUnableToComplete();

    const [postClearWorklistTasks] = usePostClearWorklistTasksMutation();
    const onClearTasksClick = (taskIds: string[]) => {
        void (async () => {
            await postClearWorklistTasks({ taskIds }).unwrap();
            await dispatch(fetchWorklistTasks());
        })();
    };

    return (
        <>
            <FullscreenLoading
                isShowing={!selectedReferral}
                loadingText="Loading referral information..."
            />
            <RebillingDialog referralDetails={referralDetails} />
            {pendingStatus === PendingReferralStatus.PENDING &&
                selectedReferral &&
                referralDetails && (
                    <PendingIntakeDialog
                        isOpen={showRenewalDialog}
                        referralId={referralId}
                        payerId={referralDetails.payerId}
                        selectedReferral={selectedReferral as PendingReferral}
                        referralDetails={referralDetails}
                        onClose={() => setShowRenewalDialog(false)}
                    />
                )}
            <UpdateNeedsByDateDialog
                isOpen={!!(showNeedsByDateDialog && referralDetails)}
                onClose={(beginConversation) => {
                    setShowNeedsByDateDialog(false);
                    if (beginConversation) {
                        startConversation(ConversationContexts.NEEDS_BY_DATE);
                    }
                }}
                referralDetails={referralDetails}
            />
            <LogFillActivityDialog
                isOpen={showLogFillActivityDialog}
                referralDetails={referralDetails}
                onClose={() => setShowLogFillActivityDialog(false)}
            />
            <BlockTasksDialog
                isOpen={!!taskToBlock}
                worklistTask={taskToBlock}
                onClose={() => setTaskToBlock(undefined)}
            />
            {!!showConvertBiosimilarDialog && referralDetails && (
                <ConvertBiosimilarDialog
                    isOpen
                    onClose={() => setShowConvertBiosimilarDialog(false)}
                    referralDetails={referralDetails}
                    memberDetails={memberDetails}
                    sourceProduct={productMap?.[referralDetails.productServiceOid]}
                    worklistTask={
                        showConvertBiosimilarDialog === true
                            ? openConversionTask
                            : showConvertBiosimilarDialog
                    }
                />
            )}
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title="Referral Details"
                            id="referral-details-page-title-button"
                            onClick={goBack}
                        />
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                additionalMenuItems={additionalMenuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                label="Actions"
                            />
                        </div>
                    </>
                }
                rowTwoContentHeader={getPendingReferralInlineMessage()}
                rowThreeContentHeader={getRowThreeContentHeader()}
                rowFourContentHeader={
                    referralDetails && (
                        <>
                            <FormTitleLabelValuePair
                                label={
                                    <ProductLabel
                                        product={
                                            productMap?.[referralDetails.productServiceOid]
                                                ?.description
                                        }
                                        isBrand={referralDetails.isBrand}
                                    />
                                }
                                value={referralDetails.strength}
                            />
                            <HeaderLabelValuePair
                                label="Estimated Quantity"
                                value={
                                    referralDetails.qty != null ? (
                                        <span
                                            className="flex__gap--x-small"
                                            style={{ alignItems: 'center' }}
                                        >
                                            <span>
                                                {addQtyUnits(
                                                    referralDetails.qty,
                                                    getEstDispensedQtyUnits(productConfig),
                                                )}
                                            </span>
                                            {productConfig?.multistrengthFlag && (
                                                <MultiStrengthTooltip />
                                            )}
                                        </span>
                                    ) : undefined
                                }
                            />
                            <HeaderLabelValuePair
                                label="Refills"
                                value={referralDetails.refills?.toLocaleString()}
                            />
                            {showFullPatientDetails && (
                                <HeaderLabelValuePair
                                    label="Name"
                                    value={formatNameLastFirst(
                                        referralDetails.memberLastName,
                                        referralDetails.memberFirstName,
                                    )}
                                />
                            )}
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={payerSegmentMap?.[referralDetails.segmentId ?? '']?.name}
                            />
                            <HeaderLabelValuePair
                                label="Place Of Service"
                                value={placeOfServiceMap?.[referralDetails.placeOfService ?? '']}
                            />
                            <HeaderLabelValuePair
                                label="Billing"
                                value={capitalizeString(referralDetails.benefit)}
                            />
                            <HeaderLabelValuePair
                                label="Referral Id"
                                value={referralDetails.friendlyId}
                            />
                            <HeaderLabelValuePair
                                label="Referral Status"
                                value={
                                    <ReferralAndOnboardingStatusIndicator
                                        referralStatus={referralDetails.status}
                                        enrollmentStatus={memberDetails?.enrollmentStatus}
                                        referralId={referralId}
                                    />
                                }
                                showValueTooltip={false}
                            />
                        </>
                    )
                }
                activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                onTabSelect={(tab) =>
                    navigate(
                        { ...location, hash: tab.replace(/\s/g, '-').toLocaleLowerCase() },
                        { state: location.state },
                    )
                }
                tabs={[
                    {
                        title: 'Overview',
                        mainContent: (
                            <div className="sds_details-page-overview">
                                <div
                                    className="sds_details-page-overview__container"
                                    style={{ flex: '2 0' }}
                                >
                                    <PatientDetails
                                        showFullDetails={showFullPatientDetails}
                                        selectedReferral={selectedReferral}
                                        referralDetails={referralDetails}
                                    />
                                    <PrescriberDetails prescriberInfo={prescriberDetails} />
                                    <div className="flex__gap--medium">
                                        <ReferralDetails
                                            showNeedsByDate={!(isExpired || isPendingOutreach)}
                                            selectedReferral={selectedReferral}
                                            referralDetails={referralDetails}
                                        />
                                        <ServicingDetails
                                            selectedReferral={selectedReferral}
                                            referralDetails={referralDetails}
                                        />
                                    </div>
                                    {payerFeatures?.[PayerFeatures.PLAN_NOTES] && (
                                        <HealthPlanNotes
                                            payerId={payerId}
                                            referralId={referralId}
                                            attestationRow={!isPendingIntake}
                                        />
                                    )}
                                    <EscalateClinicalConcern />
                                </div>
                                <NoticeBoard
                                    referralDetails={referralDetails}
                                    isPendingOutreach={isPendingOutreach}
                                />
                            </div>
                        ),
                    },
                    {
                        title: 'Performance',
                        rowOneContent: getPerformanceComponent(),
                    },
                    ...(showMedBillingSections
                        ? [
                              {
                                  title: 'Claims And Estimates',
                                  rowOneContent: selectedReferral && showFullPatientDetails && (
                                      <ClaimsAndEstimates
                                          referralId={referralId}
                                          payerId={payerId}
                                          referralDetails={referralDetails}
                                      />
                                  ),
                              },
                          ]
                        : []),
                    {
                        title: 'Worklist',
                        badgeCount: toDoReferralWorklistTasks?.length,
                        mainContent: (
                            <Worklist
                                tasks={referralWorklistTasks}
                                onTaskActionClick={(worklistTask, taskAction) => {
                                    if (taskAction === Worklist.WorklistTaskAction.HIDE_TASKS) {
                                        setTaskToBlock(worklistTask);
                                    } else if (
                                        isTaskTodo(worklistTask) &&
                                        worklistTask.task_type === Worklist.WorkTaskType.NBD
                                    ) {
                                        setShowNeedsByDateDialog(true);
                                    } else if (
                                        worklistTask.task_type ===
                                            Worklist.WorkTaskType.CONVERSION &&
                                        taskAction === Worklist.WorklistTaskAction.CONVERT
                                    ) {
                                        setShowConvertBiosimilarDialog(worklistTask);
                                    } else if (
                                        worklistTask.task_type ===
                                            Worklist.WorkTaskType.CONVERSION &&
                                        taskAction === Worklist.WorklistTaskAction.DO_NOT_CONVERT
                                    ) {
                                        onDoNotConvert(worklistTask);
                                    } else if (
                                        worklistTask.task_type !== Worklist.WorkTaskType.CONVERSION
                                    ) {
                                        onTaskActionClick(worklistTask);
                                    }
                                }}
                                onUnableToComplete={writeWorklist ? onUnableToComplete : undefined}
                                onClearTasksClick={writeWorklist ? onClearTasksClick : undefined}
                            />
                        ),
                    },
                    ...(referralDetails && pendingStatus !== PendingReferralStatus.DECLINED
                        ? [
                              {
                                  title: 'Messages',
                                  className: 'tabbed-grid-content',
                                  mainContent: (
                                      <Messages
                                          referralId={referralDetails?.referralId}
                                          referralFriendlyId={referralDetails?.friendlyId}
                                          memberFirstName={referralDetails?.memberFirstName}
                                          memberLastName={referralDetails?.memberLastName}
                                          benefit={referralDetails?.benefit}
                                          segmentId={referralDetails?.segmentId}
                                          conversations={conversationList}
                                          performanceEntries={performanceEntries}
                                          showNewConversationDialog={showNewConversationDialog}
                                          setShowNewConversationDialog={
                                              setShowNewConversationDialog
                                          }
                                          setShowLogFillActivityDialog={
                                              setShowLogFillActivityDialog
                                          }
                                      />
                                  ),
                                  badgeIntent: Badge.Intent.UNREAD,
                                  badgeCount: referralConversationCounts?.unread,
                              },
                          ]
                        : []),
                    ...(showFillActivity
                        ? [
                              {
                                  title: 'Fill Activity',
                                  mainContent: (
                                      <FillActivity events={events} isLoading={eventsFetching} />
                                  ),
                              },
                          ]
                        : []),
                ]}
            />
        </>
    );
};
